// Catalog.ID Client — Keybag Derivation Primitive (`derive`)
// ============================================================================
// The vault-side half of the `derive` keybag op (EFS lineage-labels plan §5b).
//
// The account holds ONE random 32-byte root secret in its key bag. Modules
// never see it: they hand the vault an `info` byte string and get back
// `HKDF-SHA512(root, info)` — 32 bytes. Because the root rides the ordinary
// keybag sync path (a `derive_root` entry, encrypted per-entry under the
// Argon2id-over-supersecret key, stored server-side and pulled by every device
// at login), every device of the account derives IDENTICAL keys from the same
// info. That property is the whole point: EFS's labeled-build KEK becomes a
// pure function of the package address, so two devices can never fork a
// lineage onto divergent KEKs.
//
// **Domain separation is enforced HERE, not by the caller.** The vault prefixes
// `info` with the authenticated calling module's identity before deriving, so
// no module can reach into another module's derivation namespace even if it
// echoes that module's info convention verbatim. Callers layer their own
// conventions INSIDE their namespace — EFS uses
// `"efs-label-kek-v1" ‖ operator_id ‖ asset_id ‖ role ‖ label` (applied
// caller-side, LB2) — and the vault guarantees only the module prefix.
//
// Rotating the root is deliberately NOT an operation here: per §5b it is an
// owner-run estate sweep (mint root_v2, re-derive, re-wrap every labeled
// package, revoke the v1 wraps), not a key-bag button. That sweep must ALSO
// re-derive every account-blob key and re-encrypt every blob under it
// (cid-encrypted-storage §4.2), since a blob's key is a pure function of the
// root and nothing else holds a copy — no sweep code exists today.
// ============================================================================

import { hkdf } from '@noble/hashes/hkdf.js';
import { sha512 } from '@noble/hashes/sha2.js';
import { encodeUtf8 } from './aes';

/** Key-bag slot holding the account's derive root. Bag-only: no public half. */
export const DERIVE_ROOT_KEY_ID = 'derive_root';

/**
 * Algorithm tag the server pins for {@link DERIVE_ROOT_KEY_ID} (mirrors
 * `KnownKeyTypes.GetRequiredAlgorithm`). Named for what the secret IS — HKDF
 * input keying material — rather than for a cipher: nothing ever loads this
 * into one. `recovery_kit`'s `SSS-GF256` and `bitcash_wallet`'s
 * `BITCASH-CASH` follow the same rule.
 */
export const DERIVE_ROOT_ALGORITHM = 'HKDF-SHA512';

/** Size of the root secret. */
export const DERIVE_ROOT_BYTES = 32;

/** Size of a derived key when the caller says nothing. */
export const DERIVED_KEY_BYTES = 32;

/**
 * The only widths {@link deriveModuleKey} will produce. 32 is the default and
 * every pinned vector's width; 64 exists so a hybrid Ed25519+ML-DSA-65 seed is
 * one call (cid-encrypted-storage §5.2), its halves laid out exactly as
 * `signingKeygen` expects. Anything else is refused rather than derived: an
 * unpinned width is a new construction, and HKDF's prefix consistency (see
 * below) means widths are not independent of each other anyway.
 */
export const DERIVED_KEY_LENGTHS = [32, 64] as const;
export type DerivedKeyLength = typeof DERIVED_KEY_LENGTHS[number];

/**
 * Ceiling on caller-supplied `info`. EFS's convention is a few hundred bytes
 * at most (a label is a lineage name, not a document); the bound exists so a
 * malformed or hostile caller cannot push unbounded bytes through the
 * envelope's digest gate and into the vault.
 */
export const MAX_DERIVE_INFO_BYTES = 4096;

/**
 * HKDF salt — a constant, because the whole construction must be
 * deterministic across devices. It is a VERSION anchor, not entropy: a future
 * derive construction changes this string so its outputs can never collide
 * with v1's, whatever the info.
 */
const DERIVE_SALT = encodeUtf8('catalog-cid-derive-v1');

/**
 * Byte that separates the module id from the caller's info. Legal module ids
 * (see {@link isValidModuleId}) cannot contain it, so the split point is
 * unique and no caller can shift bytes across the boundary to land inside
 * another module's namespace.
 */
const MODULE_SEPARATOR = 0x00;

/**
 * The module-id vocabulary: lowercase alphanumerics and dashes, 1–32 chars,
 * not dash-initial. Deliberately narrow — the id comes off the wire as the
 * envelope's `requesterId` (hub-attested and relay-signature-verified, but
 * still a string) and goes straight into the KDF, so anything that could
 * carry a separator, a case-fold collision, or unbounded length is refused
 * rather than derived under.
 */
const MODULE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,31}$/;

export function isValidModuleId(moduleId: string): boolean {
    return MODULE_ID_PATTERN.test(moduleId);
}

/**
 * Module ids no MODULE may claim (cid-encrypted-storage §4.1).
 *
 * `cid` is the vault's OWN derivation and blob namespace — its bitcash wallet
 * document, its registry-authority generation state. `shared` is held back for
 * the co-owned namespace §4.6 defers. Both are legal KDF inputs (the vault
 * derives under `cid` itself); what is refused is a module speaking for them,
 * which is why this is a SEPARATE check from {@link isValidModuleId} and is
 * enforced at three places: module registration, the `derive` handler, and the
 * blob handlers.
 */
export const RESERVED_MODULE_IDS = ['cid', 'shared'] as const;

export function isReservedModuleId(moduleId: string): boolean {
    return (RESERVED_MODULE_IDS as readonly string[]).includes(moduleId);
}

/** A fresh random root secret. */
export function generateDeriveRoot(): Uint8Array {
    const root = new Uint8Array(DERIVE_ROOT_BYTES);
    crypto.getRandomValues(root);
    return root;
}

/**
 * The module-scoped info the KDF actually consumes:
 * `utf8(moduleId) || 0x00 || info`.
 *
 * Exported so tests (and the .NET mirror's reviewers) can see the exact bytes
 * without re-deriving; callers should use {@link deriveModuleKey}.
 */
export function moduleScopedInfo(moduleId: string, info: Uint8Array): Uint8Array {
    if (!isValidModuleId(moduleId)) {
        throw new Error(`derive: illegal module id '${moduleId}'`);
    }
    const prefix = encodeUtf8(moduleId);
    const out = new Uint8Array(prefix.length + 1 + info.length);
    out.set(prefix, 0);
    out[prefix.length] = MODULE_SEPARATOR;
    out.set(info, prefix.length + 1);
    return out;
}

/**
 * `HKDF-SHA512(root, salt = DERIVE_SALT, info = moduleId || 0x00 || info)`,
 * 32 bytes by default.
 *
 * **HKDF is prefix-consistent**, and callers must know it: the first 32 bytes
 * of a 64-byte derive are IDENTICAL to the 32-byte derive under the same info,
 * because RFC 5869's Expand step emits one SHA-512 block and truncates. Two
 * keys of different widths are therefore NOT independent unless their info
 * strings differ — which §4.1's "one info string per blob" rule already
 * demands. `crypto/derive.test.ts` pins this so it cannot be discovered the
 * hard way.
 *
 * @param root        the account's 32-byte derive root (never leaves the vault)
 * @param moduleId    the AUTHENTICATED calling module's identity
 * @param info        the caller's info bytes, opaque to the vault
 * @param lengthBytes 32 (default) or 64; see {@link DERIVED_KEY_LENGTHS}
 */
export function deriveModuleKey(
    root: Uint8Array,
    moduleId: string,
    info: Uint8Array,
    lengthBytes: DerivedKeyLength = DERIVED_KEY_BYTES,
): Uint8Array {
    if (root.length !== DERIVE_ROOT_BYTES) {
        throw new Error(
            `derive: root must be ${DERIVE_ROOT_BYTES} bytes (got ${root.length})`);
    }
    if (info.length === 0) {
        throw new Error('derive: info must not be empty');
    }
    if (info.length > MAX_DERIVE_INFO_BYTES) {
        throw new Error(
            `derive: info is ${info.length} bytes (max ${MAX_DERIVE_INFO_BYTES})`);
    }
    if (!(DERIVED_KEY_LENGTHS as readonly number[]).includes(lengthBytes)) {
        throw new Error(
            `derive: lengthBytes must be one of ${DERIVED_KEY_LENGTHS.join(', ')} `
            + `(got ${lengthBytes})`);
    }
    // moduleScopedInfo validates the module id.
    return hkdf(sha512, root, DERIVE_SALT, moduleScopedInfo(moduleId, info), lengthBytes);
}
